import { useAuthStore } from "../stores/authStore";

/**
 * Hook to check if the current user has a specific permission
 * @param module - The module name (e.g., "catalog", "profile", "ssp")
 * @param action - The action name (e.g., "create", "view", "update", "delete")
 * @returns true if user has the permission, false otherwise
 */
export const useHasPermission = (module: string, action: string): boolean => {
  // Subscribe directly to permissions array for proper Zustand reactivity
  const permissions = useAuthStore((state) => state.user?.permissions);

  if (!permissions) return true; // Not loaded yet → default allow all
  if (permissions.length === 0) return true; // Empty array → full access
  return permissions.some((p) => p.module === module && p.action === action);
};

/**
 * Hook to get all permissions for the current user
 * @returns Array of permission objects
 */
export const useUserPermissions = () => {
  const permissions = useAuthStore((state) => state.user?.permissions || []);
  return permissions;
};

/**
 * Hook to get all permissions grouped by module
 * @returns Object with module names as keys and arrays of actions as values
 */
export const usePermissionsByModule = () => {
  const permissions = useUserPermissions();
  return permissions.reduce(
    (acc, perm) => {
      if (!acc[perm.module]) {
        acc[perm.module] = [];
      }
      acc[perm.module].push(perm.action);
      return acc;
    },
    {} as Record<string, string[]>
  );
};

/**
 * Hook to check if user can perform multiple actions
 * @param checks - Array of [module, action] tuples to check
 * @returns true only if user has ALL specified permissions
 */
export const useHasAllPermissions = (checks: [string, string][]): boolean => {
  const permissions = useAuthStore((state) => state.user?.permissions);

  if (!permissions) return true; // Not loaded → default allow
  if (permissions.length === 0) return true; // Empty → full access
  return checks.every(([module, action]) =>
    permissions.some((p) => p.module === module && p.action === action)
  );
};

/**
 * Hook to check if user can perform any of multiple actions
 * @param checks - Array of [module, action] tuples to check
 * @returns true if user has ANY of the specified permissions
 */
export const useHasAnyPermission = (checks: [string, string][]): boolean => {
  const permissions = useAuthStore((state) => state.user?.permissions);

  if (!permissions) return true; // Not loaded → default allow
  if (permissions.length === 0) return true; // Empty → full access
  return checks.some(([module, action]) =>
    permissions.some((p) => p.module === module && p.action === action)
  );
};

/**
 * Hook to check if user has access to any action in a specific module
 * @param module - The module name (e.g., "ssp", "catalog", "profile")
 * @returns true if user has any permission for this module
 */
export const useHasModuleAccess = (module: string): boolean => {
  const permissions = useAuthStore((state) => state.user?.permissions);

  if (!permissions) return true; // Not loaded yet → default allow all
  if (permissions.length === 0) return true; // Empty array → full access
  return permissions.some((p) => p.module === module);
};

/**
 * Hook to check if permissions have been loaded from the API
 * @returns true if permissions were fetched from the API, false if using default (allow all)
 */
export const useArePermissionsLoaded = (): boolean => {
  const hasLoaded = useAuthStore((state) => state.hasPermissionsLoaded);
  return hasLoaded();
};
